import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import { useSite } from "@/context/SiteContext";
import {
  Phone, Siren, FileWarning, ClipboardList, Plus, X, Trash2, Edit3,
  Lock, ShieldCheck, ArrowRight, MapPin, AlertTriangle, Utensils, Zap,
  Wrench, Flame, HeartPulse, Shield, Settings2, RotateCcw, ChevronDown,
} from "lucide-react";

// ---------- shared constants ----------
const CATEGORY_LABELS = {
  regulator: { en: "Regulator", zh: "监管机构" },
  withdrawal_authority: { en: "Withdrawal Authority", zh: "回收授权" },
  police: { en: "Police", zh: "警方" },
  fire: { en: "Fire", zh: "火患" },
  ambulance: { en: "Ambulance", zh: "救护车" },
  electricity: { en: "Electricity", zh: "供电" },
  water: { en: "Water", zh: "供水" },
  gas: { en: "Gas", zh: "供煤" },
  machine_maintenance: { en: "Machine Maintenance", zh: "机器维护" },
  onsite: { en: "Onsite", zh: "现场" },
  other: { en: "Other", zh: "其他" },
};

const NCR_TYPES = [
  { code: "critical_limit", label: "Exceeded critical limit" },
  { code: "oprp", label: "Not meeting action criteria for OPRP(s)" },
  { code: "incoming_rejection", label: "Incoming material rejection" },
  { code: "goods_returned", label: "Goods Returned from Customer" },
  { code: "quality_deteriorated", label: "Product Quality Deteriorated from Store" },
  { code: "emergency_incident", label: "Emergency Incident" },
  { code: "other", label: "Others" },
];

const DISPOSITION_OPTIONS = [
  { code: "concession", label: "Concession acceptance / use as-is" },
  { code: "rework", label: "Rework" },
  { code: "reject", label: "Reject the affected quantity" },
  { code: "destroyed", label: "Destroyed and/or disposed as waste" },
  { code: "other", label: "Others" },
];

const CAR_TYPES = [
  { code: "noncompliance_authorities",     label: "Noncompliance from Authorities" },
  { code: "customer_complaints",           label: "Customer Complaints" },
  { code: "internal_audit",                label: "Internal Audit" },
  { code: "internal_complaints",           label: "Internal Complaints" },
  { code: "complaints_external_providers", label: "Complaints to External Providers" },
  { code: "other",                         label: "Others" },
];

const ROLE_WRITER = ["owner", "manager"];
const ROLE_REPORTER = ["owner", "manager", "supervisor"];

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
  window.URL.revokeObjectURL(url);
}

// In-app confirm dialog (replaces window.confirm for verify actions).
// Portaled to body with very high z-index so it stacks above the parent modal.
function ConfirmDialog({
  open, title, message,
  confirmLabel = "Confirm", cancelLabel = "Cancel",
  busy = false, onConfirm, onCancel,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onCancel?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);
  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(60, 40, 24, 0.7)", zIndex: 10000 }}
      onClick={busy ? undefined : onCancel}
      data-testid="emergency-confirm-dialog">
      <div className="vb-card w-full p-5"
        style={{ maxWidth: 420 }}
        onClick={(e) => e.stopPropagation()}>
        <h3 className="vb-heading text-lg mb-2" style={{ color: "var(--vb-text-2)" }}>
          {title}
        </h3>
        <p className="text-sm opacity-80 mb-4" style={{ color: "var(--vb-text-2)" }}>
          {message}
        </p>
        <div className="flex gap-2 justify-end">
          <button className="vb-btn ghost text-sm" onClick={onCancel} disabled={busy}
            data-testid="emergency-confirm-cancel">{cancelLabel}</button>
          <button className="vb-btn text-sm" onClick={onConfirm} disabled={busy}
            data-testid="emergency-confirm-ok">
            {busy ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// =====================================================================
//  TAB: CONTACT DIRECTORY  (crisis-oriented layout)
//    Layer 1: Critical Response Bar (Fire / Medical / Police — always visible)
//    Layer 2: Scenario Playbooks (accordions: Food safety / Utility / Equipment / Other)
//    Layer 3: "Manage all contacts" drawer with the flat editable table
// =====================================================================

const SCENARIO_ICONS = {
  utensils: Utensils, zap: Zap, wrench: Wrench, "alert-triangle": AlertTriangle,
};

const CRITICAL_BAR = [
  {
    category: "fire",
    icon: Flame,
    title: "FIRE / SMOKE",
    accent: "#7F1D1D",
    instruction: "Evacuate first. Call from the assembly point. Use extinguisher only if trained.",
  },
  {
    category: "ambulance",
    icon: HeartPulse,
    title: "MEDICAL / INJURY",
    accent: "#B91C1C",
    instruction: "Apply first aid. Keep area clear. Send a runner to meet ambulance at the gate.",
  },
  {
    category: "police",
    icon: Shield,
    title: "CRIME / SECURITY",
    accent: "#7F1D1D",
    instruction: "Secure the scene. Do not move evidence. Note suspect description.",
  },
];

function IncidentPlaybooksTab({ contacts, scenarios, loading, siteId, canEdit, onChanged }) {
  const { confirm } = useDialog();
  const [editingContact, setEditingContact] = useState(null);    // contact | "new" | null
  const [editingSteps, setEditingSteps] = useState(null);        // scenario code | null
  const [err, setErr] = useState("");

  const onDelete = async (c) => {
    if (!await confirm({ title: "Delete contact?", message: `Delete "${c.description_en}"?`, danger: true, confirmLabel: "Delete" })) return;
    try { await api.delete(`/emergency/contacts/${c.id}`); onChanged(); }
    catch (e) { setErr(e?.response?.data?.detail || "Delete failed"); }
  };

  if (loading) return <div className="text-sm opacity-60">Loading playbooks…</div>;

  return (
    <div data-testid="emergency-playbooks-tab">
      <p className="text-sm opacity-70 max-w-2xl mb-3">
        Step-by-step response cards for the most common incidents. Owners and managers can customise the steps and link contacts per scenario.
      </p>
      {err && <div className="vb-pill err mb-3" data-testid="emergency-playbooks-error">{err}</div>}

      {/* Scenario Playbooks */}
      <div className="space-y-3" data-testid="emergency-playbooks">
        {scenarios.map((s, idx) => {
          // Contacts shown under this scenario: union of (category match) + (scenario membership).
          const scenarioContacts = contacts.filter((c) => {
            const inCategory = (s.categories || []).includes(c.category);
            const inScenarios = (c.scenarios || []).includes(s.code);
            return inCategory || inScenarios;
          });
          return (
            <PlaybookCard
              key={s.code}
              scenario={s}
              contacts={scenarioContacts}
              allContacts={contacts}
              siteId={siteId}
              canEdit={canEdit}
              defaultOpen={idx === 0}
              onEditSteps={() => setEditingSteps(s.code)}
              onLinkContact={async (cid) => {
                const c = contacts.find((x) => x.id === cid);
                if (!c) return;
                const next = Array.from(new Set([...(c.scenarios || []), s.code]));
                try {
                  await api.put(`/emergency/contacts/${cid}/scenarios`, { scenarios: next });
                  onChanged();
                } catch (e) {
                  setErr(e?.response?.data?.detail || "Failed to link contact");
                }
              }}
              onUnlinkContact={async (c) => {
                // Only unlink if it was added via scenarios array (not via category).
                if (!(c.scenarios || []).includes(s.code)) return;
                const next = (c.scenarios || []).filter((x) => x !== s.code);
                try {
                  await api.put(`/emergency/contacts/${c.id}/scenarios`, { scenarios: next });
                  onChanged();
                } catch (e) {
                  setErr(e?.response?.data?.detail || "Failed to unlink contact");
                }
              }}
              onEditContact={(c) => setEditingContact(c)}
              onDeleteContact={onDelete}
            />
          );
        })}
      </div>

      {/* Edit step modal */}
      <PlaybookStepsModal
        isOpen={editingSteps !== null}
        scenario={scenarios.find((s) => s.code === editingSteps)}
        onClose={() => setEditingSteps(null)}
        onSaved={() => { setEditingSteps(null); onChanged(); }}
      />

      {/* Contact CRUD modal */}
      <ContactModal
        isOpen={editingContact !== null}
        existing={editingContact && editingContact.id ? editingContact : null}
        presetCategory={editingContact?.presetCategory}
        siteId={siteId}
        onClose={() => setEditingContact(null)}
        onSaved={() => { setEditingContact(null); onChanged(); }}
      />
    </div>
  );
}

function ContactDirectoryTab({ contacts, loading, siteId, canEdit, onChanged }) {
  const { confirm } = useDialog();
  const [editingContact, setEditingContact] = useState(null);
  const [showManageDrawer, setShowManageDrawer] = useState(false);
  const [err, setErr] = useState("");

  const onDelete = async (c) => {
    if (!await confirm({ title: "Delete contact?", message: `Delete "${c.description_en}"?`, danger: true, confirmLabel: "Delete" })) return;
    try { await api.delete(`/emergency/contacts/${c.id}`); onChanged(); }
    catch (e) { setErr(e?.response?.data?.detail || "Delete failed"); }
  };

  // Group by category — phonebook style. Preserve the order from CATEGORY_LABELS
  // so the table reads predictably regardless of contact insertion order.
  const groupedByCategory = useMemo(() => {
    const groups = {};
    contacts.forEach((c) => {
      const cat = c.category || "other";
      (groups[cat] = groups[cat] || []).push(c);
    });
    // Sort each group by description for stable rendering.
    Object.values(groups).forEach((arr) => arr.sort((a, b) =>
      (a.description_en || "").localeCompare(b.description_en || "")
    ));
    return groups;
  }, [contacts]);

  const orderedCategories = useMemo(() => {
    const known = Object.keys(CATEGORY_LABELS);
    const unknown = Object.keys(groupedByCategory).filter((c) => !known.includes(c));
    return [...known.filter((c) => groupedByCategory[c]?.length), ...unknown];
  }, [groupedByCategory]);

  if (loading) return <div className="text-sm opacity-60">Loading contacts…</div>;

  return (
    <div data-testid="emergency-contacts-tab">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm opacity-70 max-w-2xl">
          Full on-call directory grouped by category — phonebook style. The 3 critical hotlines
          (Police / Fire / Ambulance) are always visible at the top of the page.
        </p>
        {canEdit && (
          <button className="vb-btn ghost text-sm" onClick={() => setShowManageDrawer(true)}
            data-testid="emergency-manage-contacts">
            <Settings2 size={14} /> Manage all contacts
          </button>
        )}
      </div>
      {err && <div className="vb-pill err mb-3" data-testid="emergency-contacts-error">{err}</div>}

      {contacts.length === 0 ? (
        <div className="vb-card p-8 text-center opacity-70" data-testid="emergency-contacts-empty">
          <Phone size={36} className="mx-auto opacity-40 mb-3" />
          <p className="text-sm mb-1">No contacts yet for this site.</p>
          {canEdit && (
            <p className="text-xs opacity-70">
              Click <strong>Manage all contacts</strong> above to add the first on-call number.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4" data-testid="emergency-contacts-grouped">
          {orderedCategories.map((cat) => {
            const rows = groupedByCategory[cat] || [];
            if (!rows.length) return null;
            const label = CATEGORY_LABELS[cat]?.en || cat;
            return (
              <div key={cat} className="vb-card p-0 overflow-hidden" data-testid={`contact-group-${cat}`}>
                <div className="px-5 py-3 flex items-center justify-between"
                  style={{ borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                  <strong className="text-sm" style={{ color: "var(--vb-text-2)" }}>{label}</strong>
                  <span className="text-xs opacity-60">{rows.length} contact{rows.length === 1 ? "" : "s"}</span>
                </div>
                <ul className="divide-y" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
                  {rows.map((c) => (
                    <li key={c.id} className="px-5 py-3 flex items-center gap-3 flex-wrap"
                      data-testid={`contact-row-${c.id}`}>
                      <div className="flex-1 min-w-[200px]">
                        <div className="text-sm font-medium">{c.description_en}</div>
                        {c.description_zh && <div className="text-[11px] opacity-60">{c.description_zh}</div>}
                      </div>
                      <a className="vb-link text-sm font-medium" href={`tel:${c.phone}`}
                        data-testid={`contact-phone-${c.id}`}>
                        <Phone size={12} className="inline-block mr-1 -mt-0.5" />{c.phone}
                      </a>
                      {canEdit && (
                        <div className="flex gap-1">
                          <button className="vb-btn ghost text-[10px] !p-1" title="Edit"
                            onClick={() => setEditingContact(c)}
                            data-testid={`contact-edit-${c.id}`}>
                            <Edit3 size={12} />
                          </button>
                          <button className="vb-btn ghost text-[10px] !p-1" title="Delete"
                            onClick={() => onDelete(c)}
                            data-testid={`contact-delete-${c.id}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}

      {/* Contact CRUD modal */}
      <ContactModal
        isOpen={editingContact !== null}
        existing={editingContact && editingContact.id ? editingContact : null}
        presetCategory={editingContact?.presetCategory}
        siteId={siteId}
        onClose={() => setEditingContact(null)}
        onSaved={() => { setEditingContact(null); onChanged(); }}
      />

      {/* Manage-all drawer */}
      <ManageContactsDrawer
        isOpen={showManageDrawer}
        contacts={contacts}
        siteId={siteId}
        onClose={() => setShowManageDrawer(false)}
        onEdit={(c) => { setShowManageDrawer(false); setEditingContact(c); }}
        onDelete={onDelete}
        onSaved={onChanged}
      />
    </div>
  );
}

function CriticalBar({ byCategory }) {
  return (
    <div className="grid md:grid-cols-3 gap-3" data-testid="emergency-critical-bar">
      {CRITICAL_BAR.map((card) => {
        const c = (byCategory[card.category] || [])[0];
        const Icon = card.icon;
        return (
          <div key={card.category} className="rounded-xl p-4 text-white"
            style={{ background: card.accent }}
            data-testid={`emergency-critical-${card.category}`}>
            <div className="flex items-center gap-2 mb-2">
              <Icon size={20} />
              <div className="text-xs font-bold tracking-widest uppercase opacity-90">{card.title}</div>
            </div>
            {c ? (
              <>
                <div className="text-[28px] font-black leading-none mb-1 tabular-nums">
                  {c.contact_number}
                </div>
                <div className="text-xs opacity-90">
                  {c.contact_person || c.description_en}
                </div>
              </>
            ) : (
              <div className="text-sm opacity-80">No contact configured</div>
            )}
            <div className="text-xs opacity-90 mt-3 leading-snug border-t border-white/20 pt-2">
              {card.instruction}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function PlaybookCard({
  scenario, contacts, allContacts, siteId, canEdit, defaultOpen,
  onEditSteps, onLinkContact, onUnlinkContact, onEditContact, onDeleteContact,
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [pickerOpen, setPickerOpen] = useState(false);
  const pickerBtnRef = useRef(null);
  const [pickerPos, setPickerPos] = useState(null);
  const Icon = SCENARIO_ICONS[scenario.icon] || AlertTriangle;
  const hasNCR = scenario.show_raise_ncr;

  // Available = master list minus already-shown-here.
  const showingIds = new Set(contacts.map((c) => c.id));
  const linkable = (allContacts || []).filter((c) => !showingIds.has(c.id));

  // Position the portal dropdown relative to the trigger button & viewport.
  useEffect(() => {
    if (!pickerOpen) { setPickerPos(null); return; }
    const update = () => {
      const el = pickerBtnRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const PICKER_W = 280;
      const MARGIN = 8;
      const left = Math.max(MARGIN, Math.min(window.innerWidth - PICKER_W - MARGIN, r.right - PICKER_W));
      const spaceBelow = window.innerHeight - r.bottom - MARGIN;
      const spaceAbove = r.top - MARGIN;
      // Prefer below; flip above only if below is cramped AND above has more room.
      const flipUp = spaceBelow < 200 && spaceAbove > spaceBelow;
      const available = flipUp ? spaceAbove : spaceBelow;
      const maxH = Math.min(360, Math.max(120, available));
      setPickerPos({
        left,
        top: flipUp ? Math.max(MARGIN, r.top - maxH - 4) : r.bottom + 4,
        width: PICKER_W,
        maxHeight: maxH,
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [pickerOpen]);

  return (
    <div className="vb-card p-0 overflow-hidden" data-testid={`emergency-playbook-${scenario.code}`}>
      <button
        className="w-full p-4 flex items-start gap-3 text-left hover:bg-black/[0.02] transition"
        onClick={() => setOpen(!open)}
        data-testid={`emergency-playbook-toggle-${scenario.code}`}
      >
        <div className="rounded-xl p-2 flex-shrink-0"
          style={{ background: "var(--vb-bg-2)", color: "var(--vb-text-2)" }}>
          <Icon size={20} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-base" style={{ color: "var(--vb-text-2)" }}>
            {scenario.title}
          </div>
          <div className="text-xs opacity-60 mt-0.5">
            {scenario.title_zh} · {contacts.length} contact{contacts.length !== 1 ? "s" : ""}
            {scenario.is_overridden && (
              <span className="ml-2 vb-pill text-[10px]"
                style={{ background: "var(--vb-bg-2)", color: "var(--vb-text-2)" }}>
                Custom steps
              </span>
            )}
          </div>
        </div>
        <ChevronDown size={18}
          style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 0.2s" }} />
      </button>

      {open && (
        <div className="p-4 pt-0 grid md:grid-cols-2 gap-4 border-t"
          style={{ borderColor: "var(--vb-border)" }}>
          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-xs font-bold uppercase tracking-wider"
                style={{ color: "var(--vb-text-2)" }}>What to do</h4>
              {canEdit && (
                <button className="vb-btn ghost text-sm" onClick={onEditSteps}
                  data-testid={`emergency-playbook-edit-steps-${scenario.code}`}>
                  <Edit3 size={11} /> Edit steps
                </button>
              )}
            </div>
            <ol className="space-y-1.5 text-sm">
              {(scenario.steps || []).map((step, i) => (
                <li key={`step-${i}`} className="flex gap-2"
                  data-testid={`emergency-playbook-step-${scenario.code}-${i}`}>
                  <span className="rounded-full text-[10px] font-bold w-5 h-5 flex items-center justify-center flex-shrink-0"
                    style={{ background: "var(--vb-accent)", color: "white" }}>{i + 1}</span>
                  <span style={{ color: "var(--vb-text-2)" }}>{step}</span>
                </li>
              ))}
            </ol>
            {hasNCR && (
              <a href="#ncr-tab"
                className="vb-btn mt-3 text-sm inline-flex items-center gap-1"
                onClick={(e) => { e.preventDefault(); window.dispatchEvent(new CustomEvent("emergency-jump-ncr")); }}
                data-testid={`emergency-playbook-raise-ncr-${scenario.code}`}>
                <FileWarning size={12} /> Raise an NCR
              </a>
            )}
          </div>

          {/* Contacts */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <h4 className="text-xs font-bold uppercase tracking-wider"
                style={{ color: "var(--vb-text-2)" }}>Who to call</h4>
              {canEdit && linkable.length > 0 && (
                <div className="relative">
                  <button ref={pickerBtnRef} className="vb-btn ghost text-sm"
                    onClick={() => setPickerOpen(!pickerOpen)}
                    data-testid={`emergency-playbook-link-contact-${scenario.code}`}>
                    <Plus size={11} /> Add contact
                    <ChevronDown size={11} />
                  </button>
                  {pickerOpen && pickerPos && createPortal(
                    <>
                      <div className="fixed inset-0"
                        style={{ zIndex: 9998 }}
                        onClick={() => setPickerOpen(false)} />
                      <div className="fixed vb-card p-2 overflow-y-auto"
                        style={{
                          left: pickerPos.left,
                          top: pickerPos.top,
                          width: pickerPos.width,
                          maxHeight: pickerPos.maxHeight,
                          zIndex: 9999,
                          background: "var(--vb-card)",
                          boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
                        }}
                        data-testid={`emergency-playbook-picker-${scenario.code}`}>
                        <div className="text-[10px] uppercase tracking-wider opacity-60 px-2 pb-1 sticky top-0"
                          style={{ background: "var(--vb-card)" }}>
                          Link an existing contact
                        </div>
                        {linkable.map((c) => (
                          <button
                            key={c.id}
                            onClick={() => { setPickerOpen(false); onLinkContact(c.id); }}
                            className="w-full text-left px-2 py-1.5 text-sm rounded hover:bg-black/[0.04]"
                            data-testid={`emergency-playbook-pick-${scenario.code}-${c.id}`}>
                            <div className="font-semibold truncate"
                              style={{ color: "var(--vb-text-2)" }}>
                              {c.description_en}
                            </div>
                            <div className="text-xs opacity-70 truncate">
                              {c.contact_number} · {c.contact_person || "—"}
                            </div>
                          </button>
                        ))}
                      </div>
                    </>,
                    document.body
                  )}
                </div>
              )}
            </div>
            {contacts.length === 0 ? (
              <div className="text-xs opacity-60 italic">
                No contact linked yet. Use <b>Add contact</b> above to link one from your master list,
                or add a new contact in <b>Manage all contacts</b>.
              </div>
            ) : (
              <div className="space-y-2">
                {contacts.map((c) => (
                  <PlaybookContactRow key={c.id} c={c} scenarioCode={scenario.code} canEdit={canEdit}
                    onUnlink={() => onUnlinkContact(c)} />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PlaybookContactRow({ c, scenarioCode, canEdit, onUnlink }) {
  const linkedViaScenario = (c.scenarios || []).includes(scenarioCode);
  return (
    <div className="vb-card p-2.5 flex items-center gap-3 w-full min-w-0 overflow-hidden"
      style={{ background: "var(--vb-bg-2)" }}
      data-testid={`emergency-contact-${c.id}`}>
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold break-words" style={{ color: "var(--vb-text-2)" }}>
          {c.description_en}
          {c.description_zh && (
            <span className="opacity-50 font-normal"> / {c.description_zh}</span>
          )}
        </div>
        <div className="text-[11px] opacity-70 truncate">
          {c.contact_person || "—"}{c.department ? ` · ${c.department}` : ""}
        </div>
        <div className="text-lg font-bold mt-0.5 leading-none tabular-nums" style={{ color: "var(--vb-text-2)" }}>
          {c.contact_number}
        </div>
        {c.office_handphone && (
          <div className="text-[11px] opacity-60 mt-0.5 truncate">Alt: {c.office_handphone}</div>
        )}
      </div>
      <div className="flex flex-col items-end gap-1 shrink-0">
        {c.is_locked
          ? <Lock size={12} style={{ color: "var(--vb-text-2)", opacity: 0.5 }} title="National contact" />
          : <Phone size={12} style={{ color: "var(--vb-text-2)", opacity: 0.5 }} />}
        {canEdit && linkedViaScenario && (
          <button className="vb-btn ghost text-[10px] !p-1" onClick={onUnlink}
            title="Unlink from this scenario"
            data-testid={`emergency-playbook-unlink-${c.id}`}>
            <X size={10} />
          </button>
        )}
      </div>
    </div>
  );
}

function PlaybookStepsModal({ isOpen, scenario, onClose, onSaved }) {
  const { confirm } = useDialog();
  const [steps, setSteps] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (isOpen && scenario) {
      setSteps([...(scenario.steps || [])]);
      setErr("");
    }
  }, [isOpen, scenario]);

  if (!isOpen || !scenario) return null;

  const save = async () => {
    const clean = steps.filter((s) => s && s.trim());
    if (clean.length === 0) { setErr("Add at least one step."); return; }
    setBusy(true); setErr("");
    try {
      await api.put(`/emergency/playbooks/${scenario.code}`, { steps: clean });
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const reset = async () => {
    if (!await confirm({ title: "Reset steps?", message: "Reset to the default steps for this scenario? Your custom steps will be discarded.", danger: true, confirmLabel: "Reset to default" })) return;
    setBusy(true);
    try {
      await api.delete(`/emergency/playbooks/${scenario.code}`);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(60, 40, 24, 0.6)", zIndex: 9999 }}
      data-testid="emergency-playbook-steps-modal">
      <div className="vb-card max-w-xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>
              Edit playbook steps
            </h2>
            <div className="text-xs opacity-70 mt-0.5">{scenario.title}</div>
          </div>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X size={20} /></button>
        </div>
        <p className="text-xs opacity-70 mb-3">
          One step per line. Keep them short, action-first ("Stop the machine" not "The machine
          should be stopped"). These instructions appear next to the contacts in this scenario.
        </p>

        <div className="space-y-2">
          {steps.map((s, i) => (
            <div key={`s-${i}`} className="flex gap-2">
              <span className="rounded-full text-xs font-bold w-6 h-6 flex items-center justify-center flex-shrink-0 mt-1"
                style={{ background: "var(--vb-accent)", color: "white" }}>{i + 1}</span>
              <textarea
                rows={2} className="vb-input flex-1 text-sm" value={s}
                onChange={(e) => { const c = [...steps]; c[i] = e.target.value; setSteps(c); }}
                data-testid={`emergency-playbook-step-input-${i}`}
              />
              <button className="vb-btn ghost text-sm self-start mt-1"
                onClick={() => setSteps(steps.filter((_, idx) => idx !== i))}>
                <X size={12} />
              </button>
            </div>
          ))}
          <button className="vb-btn ghost text-sm"
            onClick={() => setSteps([...steps, ""])}
            data-testid="emergency-playbook-add-step">+ Add step</button>
        </div>

        {err && <div className="vb-pill err mt-3">{err}</div>}

        <div className="flex gap-2 justify-between items-center mt-4">
          {scenario.is_overridden ? (
            <button className="vb-btn ghost text-sm" onClick={reset} disabled={busy}
              data-testid="emergency-playbook-reset">
              <RotateCcw size={12} /> Reset to default
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button className="vb-btn ghost text-sm" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="vb-btn text-sm" onClick={save} disabled={busy}
              data-testid="emergency-playbook-save">
              {busy ? "Saving…" : "Save steps"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function ManageContactsDrawer({ isOpen, contacts, siteId, onClose, onDelete, onSaved }) {
  const [addingRow, setAddingRow] = useState(null);    // form state for inline NEW row
  const [editingId, setEditingId] = useState(null);    // id of row being edited
  const [editForm, setEditForm] = useState(null);      // working copy while editing
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // ESC to close + body scroll lock while open.
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [isOpen, onClose]);

  const startAdd = () => {
    setEditingId(null); setEditForm(null);
    setAddingRow({
      scope: "site", category: "other",
      description_en: "", description_zh: "",
      contact_person: "", department: "",
      contact_number: "", office_handphone: "",
    });
    setErr("");
  };
  const cancelAdd = () => { setAddingRow(null); setErr(""); };
  const saveNew = async () => {
    if (!addingRow.description_en.trim() || !addingRow.contact_number.trim()) {
      setErr("Description and number are required."); return;
    }
    setBusy(true); setErr("");
    try {
      await api.post("/emergency/contacts", {
        ...addingRow,
        site_id: addingRow.scope === "site" ? siteId : null,
        sort_order: 100,
      });
      setAddingRow(null);
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const startEdit = (c) => {
    setAddingRow(null);
    setEditingId(c.id);
    setEditForm({
      scope: c.scope || "tenant",
      site_id: c.site_id || null,
      category: c.category,
      description_en: c.description_en || "",
      description_zh: c.description_zh || "",
      contact_person: c.contact_person || "",
      department: c.department || "",
      contact_number: c.contact_number || "",
      office_handphone: c.office_handphone || "",
      sort_order: c.sort_order ?? 100,
      scenarios: c.scenarios || [],
      is_locked: !!c.is_locked,
    });
    setErr("");
  };
  const cancelEdit = () => { setEditingId(null); setEditForm(null); setErr(""); };
  const saveEdit = async () => {
    if (!editForm.description_en.trim() || !editForm.contact_number.trim()) {
      setErr("Description and number are required."); return;
    }
    setBusy(true); setErr("");
    try {
      const body = {
        scope: editForm.is_locked ? "region" : editForm.scope,
        site_id: editForm.scope === "site" ? (siteId || editForm.site_id) : null,
        category: editForm.category,
        description_en: editForm.description_en,
        description_zh: editForm.description_zh,
        contact_person: editForm.contact_person,
        department: editForm.department,
        contact_number: editForm.contact_number,
        office_handphone: editForm.office_handphone,
        sort_order: editForm.sort_order,
        scenarios: editForm.scenarios,
      };
      await api.put(`/emergency/contacts/${editingId}`, body);
      cancelEdit();
      onSaved?.();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 flex items-stretch justify-end"
      style={{ background: "rgba(60, 40, 24, 0.6)", zIndex: 9999 }}
      onClick={onClose}
      data-testid="emergency-manage-contacts-drawer">
      <div
        className="w-full flex flex-col"
        style={{ background: "var(--vb-bg)", maxWidth: "min(1100px, 95vw)" }}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b sticky top-0 z-10"
          style={{ borderColor: "var(--vb-border)", background: "var(--vb-bg)" }}>
          <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>
            Manage all contacts
          </h2>
          <div className="flex gap-2 items-center">
            <button className="vb-btn text-sm" onClick={startAdd} disabled={!!addingRow || !!editingId}
              data-testid="emergency-manage-add">
              <Plus size={14} /> Add contact
            </button>
            <button onClick={onClose} className="vb-btn ghost text-sm"
              data-testid="emergency-manage-close"
              aria-label="Close drawer">
              <X size={18} />
            </button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          <p className="text-xs opacity-70 mb-3">
            Click the pencil to edit any row inline. Click <b>Add contact</b> to insert a new row.
            Press <kbd className="px-1 py-0.5 rounded text-[10px]"
              style={{ background: "var(--vb-bg-2)" }}>Esc</kbd> or click outside to close.
          </p>
          {err && <div className="vb-pill err mb-3" data-testid="emergency-manage-error">{err}</div>}
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider opacity-70 border-b"
                style={{ borderColor: "var(--vb-border)" }}>
                <th className="py-2">Description</th>
                <th>Category</th>
                <th>Person / Dept</th>
                <th>Number</th>
                <th>Scope</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {addingRow && (
                <ManageEditableRow
                  form={addingRow} setForm={setAddingRow}
                  onSave={saveNew} onCancel={cancelAdd}
                  busy={busy} testIdPrefix="emergency-manage-new"
                />
              )}
              {contacts.map((c) => (
                editingId === c.id ? (
                  <ManageEditableRow
                    key={c.id}
                    form={editForm} setForm={setEditForm}
                    onSave={saveEdit} onCancel={cancelEdit}
                    busy={busy} testIdPrefix={`emergency-manage-edit-${c.id}`}
                    isLocked={editForm?.is_locked}
                  />
                ) : (
                  <tr key={c.id} className="border-b" style={{ borderColor: "var(--vb-border)" }}>
                    <td className="py-2 pr-2">
                      <div className="font-semibold" style={{ color: "var(--vb-text-2)" }}>
                        {c.description_en}
                      </div>
                      {c.description_zh && <div className="text-xs opacity-60">{c.description_zh}</div>}
                    </td>
                    <td className="pr-2 text-xs">{CATEGORY_LABELS[c.category]?.en || c.category}</td>
                    <td className="pr-2 text-xs">
                      {c.contact_person || "—"}{c.department ? ` · ${c.department}` : ""}
                    </td>
                    <td className="pr-2 font-bold tabular-nums">{c.contact_number}</td>
                    <td className="pr-2 text-xs">
                      {c.is_locked
                        ? <span className="inline-flex items-center gap-1"><Lock size={10} /> Region</span>
                        : c.scope}
                    </td>
                    <td className="pr-2">
                      <div className="flex gap-1">
                        <button className="vb-btn ghost text-[10px] !p-1" onClick={() => startEdit(c)}
                          data-testid={`emergency-manage-edit-${c.id}`}>
                          <Edit3 size={10} />
                        </button>
                        {!c.is_locked && (
                          <button className="vb-btn ghost text-[10px] !p-1" onClick={() => onDelete(c)}
                            data-testid={`emergency-manage-delete-${c.id}`}>
                            <Trash2 size={10} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/** A row of editable cells, used for both inline-Add and inline-Edit. */
function ManageEditableRow({ form, setForm, onSave, onCancel, busy, testIdPrefix, isLocked }) {
  if (!form) return null;
  return (
    <tr className="border-b" style={{ borderColor: "var(--vb-border)", background: "var(--vb-bg-2)" }}
      data-testid={`${testIdPrefix}-row`}>
      <td className="py-2 pr-2">
        <input className="vb-input text-xs mb-1" placeholder="Description (EN)"
          value={form.description_en}
          onChange={(e) => setForm({ ...form, description_en: e.target.value })}
          data-testid={`${testIdPrefix}-desc-en`} />
        <input className="vb-input text-xs" placeholder="中文 (optional)"
          value={form.description_zh}
          onChange={(e) => setForm({ ...form, description_zh: e.target.value })} />
      </td>
      <td className="pr-2">
        <select className="vb-input text-xs" value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}
          data-testid={`${testIdPrefix}-category`}>
          {Object.entries(CATEGORY_LABELS).map(([code, lbl]) => (
            <option key={code} value={code}>{lbl.en}</option>
          ))}
        </select>
      </td>
      <td className="pr-2">
        <input className="vb-input text-xs mb-1" placeholder="Person"
          value={form.contact_person}
          onChange={(e) => setForm({ ...form, contact_person: e.target.value })} />
        <input className="vb-input text-xs" placeholder="Department"
          value={form.department}
          onChange={(e) => setForm({ ...form, department: e.target.value })} />
      </td>
      <td className="pr-2">
        <input className="vb-input text-xs mb-1" placeholder="Main"
          value={form.contact_number}
          onChange={(e) => setForm({ ...form, contact_number: e.target.value })}
          data-testid={`${testIdPrefix}-number`} />
        <input className="vb-input text-xs" placeholder="Alt / handphone"
          value={form.office_handphone}
          onChange={(e) => setForm({ ...form, office_handphone: e.target.value })} />
      </td>
      <td className="pr-2">
        {isLocked ? (
          <span className="text-[10px] inline-flex items-center gap-1 opacity-70">
            <Lock size={10} /> Region
          </span>
        ) : (
          <select className="vb-input text-xs" value={form.scope}
            onChange={(e) => setForm({ ...form, scope: e.target.value })}>
            <option value="site">This site</option>
            <option value="tenant">All sites</option>
          </select>
        )}
      </td>
      <td className="pr-2">
        <div className="flex gap-1">
          <button className="vb-btn text-[10px] !px-2 !py-1" onClick={onSave} disabled={busy}
            data-testid={`${testIdPrefix}-save`}>
            {busy ? "…" : "Save"}
          </button>
          <button className="vb-btn ghost text-[10px] !px-2 !py-1" onClick={onCancel}
            data-testid={`${testIdPrefix}-cancel`}>
            <X size={10} />
          </button>
        </div>
      </td>
    </tr>
  );
}

function ContactModal({ isOpen, existing, presetCategory, siteId, onClose, onSaved }) {
  const [form, setForm] = useState({
    scope: "site", category: "other", description_en: "", description_zh: "",
    contact_person: "", department: "", contact_number: "", office_handphone: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    if (existing) {
      setForm({
        scope: existing.scope, category: existing.category,
        description_en: existing.description_en, description_zh: existing.description_zh || "",
        contact_person: existing.contact_person || "", department: existing.department || "",
        contact_number: existing.contact_number, office_handphone: existing.office_handphone || "",
      });
    } else {
      setForm({
        scope: "site", category: presetCategory || "other", description_en: "", description_zh: "",
        contact_person: "", department: "", contact_number: "", office_handphone: "",
      });
    }
    setErr("");
  }, [isOpen, existing, presetCategory]);

  if (!isOpen) return null;

  const set = (k, v) => setForm({ ...form, [k]: v });

  const submit = async () => {
    if (!form.description_en.trim() || !form.contact_number.trim()) {
      setErr("Description and contact number are required."); return;
    }
    setBusy(true); setErr("");
    try {
      const body = { ...form, site_id: form.scope === "site" ? siteId : null, sort_order: 100 };
      if (existing) await api.put(`/emergency/contacts/${existing.id}`, body);
      else await api.post("/emergency/contacts", body);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(60, 40, 24, 0.6)", zIndex: 9999 }}
      data-testid="emergency-contact-modal">
      <div className="vb-card max-w-lg w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>
            {existing ? "Edit contact" : "Add emergency contact"}
          </h2>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X size={20} /></button>
        </div>
        <div className="grid gap-3">
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider">Scope</label>
            <select className="vb-input" value={form.scope}
              onChange={(e) => set("scope", e.target.value)}
              data-testid="emergency-contact-scope">
              <option value="site">This site only</option>
              <option value="tenant">All sites (tenant-wide)</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold mb-1 uppercase tracking-wider">Category</label>
            <select className="vb-input" value={form.category}
              onChange={(e) => set("category", e.target.value)}
              data-testid="emergency-contact-category">
              {Object.entries(CATEGORY_LABELS).map(([code, lbl]) => (
                <option key={code} value={code}>{lbl.en} / {lbl.zh}</option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wider">Description (EN)</label>
              <input className="vb-input" value={form.description_en}
                onChange={(e) => set("description_en", e.target.value)}
                placeholder="e.g. Blender #1 Maintenance"
                data-testid="emergency-contact-desc-en" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wider">Description (中文)</label>
              <input className="vb-input" value={form.description_zh}
                onChange={(e) => set("description_zh", e.target.value)}
                placeholder="e.g. 搅拌机1号维修"
                data-testid="emergency-contact-desc-zh" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wider">Contact person</label>
              <input className="vb-input" value={form.contact_person}
                onChange={(e) => set("contact_person", e.target.value)} />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wider">Department</label>
              <input className="vb-input" value={form.department}
                onChange={(e) => set("department", e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wider">Phone number</label>
              <input className="vb-input" value={form.contact_number}
                onChange={(e) => set("contact_number", e.target.value)}
                data-testid="emergency-contact-number" />
            </div>
            <div>
              <label className="block text-xs font-semibold mb-1 uppercase tracking-wider">Alt / handphone</label>
              <input className="vb-input" value={form.office_handphone}
                onChange={(e) => set("office_handphone", e.target.value)} />
            </div>
          </div>
          {err && <div className="vb-pill err" data-testid="emergency-contact-error">{err}</div>}
          <div className="flex gap-2 justify-end">
            <button className="vb-btn ghost text-sm" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="vb-btn text-sm" onClick={submit} disabled={busy}
              data-testid="emergency-contact-save">
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// =====================================================================
//  TAB: RESPONSE REPORTS
// =====================================================================
function ResponseReports({ canReport, canVerify, activeSiteId, isAllSites, visibleSites, users, onPromptNCR }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);    // "new" | report
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = isAllSites ? "" : `?site_id=${activeSiteId}`;
      const res = await api.get(`/emergency/reports${q}`);
      setReports(res.data.reports || []);
    } catch (e) {
      console.error("Reports load failed:", e);
      setErr("Failed to load reports");
    } finally { setLoading(false); }
  }, [activeSiteId, isAllSites]);
  useEffect(() => { load(); }, [load]);

  const sitesById = useMemo(
    () => Object.fromEntries(visibleSites.map((s) => [s.id, s])),
    [visibleSites],
  );

  return (
    <div data-testid="emergency-reports-tab">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm opacity-70 max-w-2xl">
          Log emergency incidents per ISO 22000 VB-P-084 (F01). Includes evacuation status, product impact,
          and verification sign-off. PDFs can be generated from the Reports page.
        </p>
        {canReport && (
          <button className="vb-btn text-sm" onClick={() => setEditing("new")}
            data-testid="emergency-report-add"
            disabled={isAllSites || !activeSiteId}
            title={(isAllSites || !activeSiteId) ? "Select a specific site in the header to enable this button" : "Add a new Emergency Response Report"}>
            <Plus size={14} /> New report
          </button>
        )}
      </div>
      {(isAllSites || !activeSiteId) && canReport && (
        <div className="vb-pill warn mb-3 text-xs" data-testid="emergency-report-site-hint">
          Heads up — please select a specific site in the header to enable the “New report” button.
        </div>
      )}
      {err && <div className="vb-pill err mb-3">{err}</div>}
      {loading && <div className="text-sm opacity-60">Loading…</div>}
      {!loading && reports.length === 0 && (
        <div className="vb-card p-8 text-center opacity-70">No emergency reports filed yet.</div>
      )}
      {!loading && reports.length > 0 && (
        <div className="grid gap-2">
          {reports.map((r) => (
            <ReportListItem key={r.id} r={r} canVerify={canVerify}
              siteName={sitesById[r.site_id]?.name || r.site_name}
              onOpen={() => setEditing(r)}
              onReload={load} />
          ))}
        </div>
      )}

      <ReportModal
        isOpen={editing !== null}
        existing={editing === "new" ? null : editing}
        siteId={editing === "new" ? activeSiteId : editing?.site_id}
        users={users}
        canVerify={canVerify}
        onClose={() => setEditing(null)}
        onSaved={(saved, opts) => {
          setEditing(null);
          load();
          if (opts?.deleted) return;
          // If product impact filled and no linked NCR yet, prompt
          if (saved && saved.affected_product_name && !saved.ncr_id) {
            onPromptNCR(saved);
          }
        }}
      />
    </div>
  );
}

function ReportListItem({ r, siteName, canVerify, onOpen, onReload }) {
  const verified = r.status === "verified";
  const downloadPdf = async (e) => {
    e.stopPropagation();
    try {
      const res = await api.get(`/reports/emergency/response/${r.id}/pdf`, { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      downloadBlob(res.data, m ? m[1] : "emergency-report.pdf");
    } catch (err) {
      console.error("PDF download failed:", err);
    }
  };
  return (
    <button onClick={onOpen}
      className="vb-card p-3 text-left hover:shadow-md transition w-full"
      data-testid={`emergency-report-${r.id}`}>
      <div className="flex items-start gap-3">
        <div className="rounded-full p-2 flex-shrink-0"
          style={{ background: verified ? "#4F8A3A" : "#B45309", color: "white" }}>
          <Siren size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm" style={{ color: "var(--vb-text-2)" }}>
              {r.nature || "Emergency incident"}
            </span>
            {verified
              ? <span className="vb-pill ok text-[10px]">Verified</span>
              : <span className="vb-pill warn text-[10px]">Draft</span>}
            {r.ncr_id && (
              <span className="vb-pill text-[10px] inline-flex items-center gap-1">
                <ArrowRight size={10} /> NCR linked
              </span>
            )}
          </div>
          <div className="text-xs opacity-70 mt-1">
            {siteName} · {(r.incident_at || "").slice(0, 16).replace("T", " ")}
            {r.affected_product_name && <> · Product: {r.affected_product_name}</>}
          </div>
        </div>
        <button onClick={downloadPdf} className="vb-btn ghost text-sm flex-shrink-0"
          data-testid={`emergency-report-pdf-${r.id}`}>
          PDF
        </button>
      </div>
    </button>
  );
}

function ReportModal({ isOpen, existing, siteId, users, canVerify, onClose, onSaved }) {
  const empty = {
    incident_at: new Date().toISOString().slice(0, 16),
    staff_present_count: 0, nature: "", declared_evacuation_time: "",
    affected_product_name: "", affected_product_quantity: "",
    conformance_yn: null, observations: "", recommended_action: "",
    responsible_person_id: "", target_completion_date: "",
    verification_action: "", assembly_point: "",
    evacuation_status: [],
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [verifyConfirmOpen, setVerifyConfirmOpen] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (existing) {
      setForm({
        ...empty, ...existing,
        responsible_person_id: existing.responsible_person_id || "",
        evacuation_status: existing.evacuation_status || [],
      });
    } else { setForm(empty); }
    setErr("");
    setVerifyConfirmOpen(false);
    setDeleteConfirmOpen(false);
  }, [isOpen, existing]);    // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const addRow = () => setForm({
    ...form,
    evacuation_status: [...form.evacuation_status, { department: "", present_count: 0, absent_count: 0, remark: "" }],
  });

  const submit = async () => {
    if (!form.nature.trim()) { setErr("Nature of emergency is required."); return; }
    if (!siteId) { setErr("No site selected."); return; }
    setBusy(true); setErr("");
    try {
      const body = {
        ...form, site_id: siteId,
        responsible_person_id: form.responsible_person_id || null,
        incident_at: form.incident_at.length === 16 ? form.incident_at + ":00Z" : form.incident_at,
      };
      const res = existing
        ? await api.put(`/emergency/reports/${existing.id}`, body).then(() => api.get(`/emergency/reports/${existing.id}`)).then((r) => r.data)
        : (await api.post("/emergency/reports", body)).data;
      onSaved(res);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!existing) return;
    setVerifyConfirmOpen(true);
  };

  const doVerify = async () => {
    setVerifyBusy(true);
    try {
      await api.post(`/emergency/reports/${existing.id}/verify`, { verification_action: form.verification_action });
      setVerifyConfirmOpen(false);
      onSaved({ ...existing, status: "verified" });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Verify failed");
      setVerifyConfirmOpen(false);
    } finally { setVerifyBusy(false); }
  };

  const askDelete = () => { if (existing) setDeleteConfirmOpen(true); };
  const doDelete = async () => {
    setDeleteBusy(true);
    try {
      await api.delete(`/emergency/reports/${existing.id}`);
      setDeleteConfirmOpen(false);
      onSaved(null, { deleted: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed");
      setDeleteConfirmOpen(false);
    } finally { setDeleteBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(60, 40, 24, 0.6)", zIndex: 9999 }}
      data-testid="emergency-report-modal">
      <div className="vb-card max-w-3xl w-full p-5 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>
            {existing ? "Emergency Response Report" : "New Emergency Response Report"}
            <span className="ml-2 text-xs opacity-60 font-normal">VB-P-084 (F01)</span>
          </h2>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X size={20} /></button>
        </div>
        <div className="grid gap-3">
          <SectionLabel>Incident Details / 事故详情</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date & time of emergency">
              <input type="datetime-local" className="vb-input" value={(form.incident_at || "").slice(0, 16)}
                onChange={(e) => set("incident_at", e.target.value)}
                data-testid="emergency-report-incident-at" />
            </Field>
            <Field label="Staff present count">
              <input type="number" min="0" className="vb-input" value={form.staff_present_count}
                onChange={(e) => set("staff_present_count", Number(e.target.value || 0))} />
            </Field>
          </div>
          <Field label="Nature of the emergency">
            <textarea className="vb-input" rows={2} value={form.nature}
              onChange={(e) => set("nature", e.target.value)}
              placeholder="e.g. Fire in oven area, evacuated all production staff"
              data-testid="emergency-report-nature" />
          </Field>
          <Field label="Declared evacuation time (24h)">
            <input className="vb-input" value={form.declared_evacuation_time}
              onChange={(e) => set("declared_evacuation_time", e.target.value)}
              placeholder="e.g. 14:32" />
          </Field>

          <SectionLabel>Product Impact / 受影响产品</SectionLabel>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Affected product name">
              <input className="vb-input" value={form.affected_product_name}
                onChange={(e) => set("affected_product_name", e.target.value)}
                data-testid="emergency-report-product" />
            </Field>
            <Field label="Affected quantity">
              <input className="vb-input" value={form.affected_product_quantity}
                onChange={(e) => set("affected_product_quantity", e.target.value)}
                placeholder="e.g. 50 kg" />
            </Field>
          </div>

          <SectionLabel>Emergency Response Procedure / 应急响应程序</SectionLabel>
          <Field label="Did the response follow procedure?">
            <div className="flex gap-3">
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="radio" checked={form.conformance_yn === true}
                  onChange={() => set("conformance_yn", true)} /> Yes
              </label>
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="radio" checked={form.conformance_yn === false}
                  onChange={() => set("conformance_yn", false)} /> No
              </label>
            </div>
          </Field>

          <SectionLabel>Observations & Recommendations / 观察与建议</SectionLabel>
          <Field label="Observations during the emergency">
            <textarea className="vb-input" rows={2} value={form.observations}
              onChange={(e) => set("observations", e.target.value)} />
          </Field>
          <Field label="Recommended action">
            <textarea className="vb-input" rows={2} value={form.recommended_action}
              onChange={(e) => set("recommended_action", e.target.value)} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Responsible person">
              <select className="vb-input" value={form.responsible_person_id}
                onChange={(e) => set("responsible_person_id", e.target.value)}>
                <option value="">— Select —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label="Target completion date">
              <input type="date" className="vb-input" value={form.target_completion_date}
                onChange={(e) => set("target_completion_date", e.target.value)} />
            </Field>
          </div>

          <SectionLabel>Staff Evacuation Status / 员工疏散情况</SectionLabel>
          <Field label="Assembly point">
            <input className="vb-input" value={form.assembly_point}
              onChange={(e) => set("assembly_point", e.target.value)}
              placeholder="e.g. Carpark exit B, west side" />
          </Field>
          <div className="space-y-2">
            {(form.evacuation_status || []).map((row, i) => (
              <div key={`evac-${i}`} className="grid grid-cols-12 gap-2 items-center">
                <input className="vb-input col-span-4" placeholder="Department" value={row.department}
                  onChange={(e) => {
                    const copy = [...form.evacuation_status]; copy[i] = { ...row, department: e.target.value };
                    set("evacuation_status", copy);
                  }} />
                <input type="number" min="0" className="vb-input col-span-2" placeholder="Present" value={row.present_count}
                  onChange={(e) => {
                    const copy = [...form.evacuation_status]; copy[i] = { ...row, present_count: Number(e.target.value || 0) };
                    set("evacuation_status", copy);
                  }} />
                <input type="number" min="0" className="vb-input col-span-2" placeholder="Absent" value={row.absent_count}
                  onChange={(e) => {
                    const copy = [...form.evacuation_status]; copy[i] = { ...row, absent_count: Number(e.target.value || 0) };
                    set("evacuation_status", copy);
                  }} />
                <input className="vb-input col-span-3" placeholder="Remark" value={row.remark}
                  onChange={(e) => {
                    const copy = [...form.evacuation_status]; copy[i] = { ...row, remark: e.target.value };
                    set("evacuation_status", copy);
                  }} />
                <button className="vb-btn ghost col-span-1 text-sm"
                  onClick={() => set("evacuation_status", form.evacuation_status.filter((_, idx) => idx !== i))}>
                  <X size={10} />
                </button>
              </div>
            ))}
            <button className="vb-btn ghost text-sm" onClick={addRow}
              data-testid="emergency-report-add-evac-row">+ Add department row</button>
          </div>

          <SectionLabel>Verification / 验证</SectionLabel>
          <Field label="Verification action">
            <textarea className="vb-input" rows={2} value={form.verification_action}
              onChange={(e) => set("verification_action", e.target.value)} />
          </Field>
          {existing?.verified_by_name && (
            <div className="text-xs opacity-70">
              Verified by <b>{existing.verified_by_name}</b> on {(existing.verified_at || "").slice(0, 10)}
            </div>
          )}

          {err && <div className="vb-pill err" data-testid="emergency-report-error">{err}</div>}
          <div className="flex gap-2 justify-end pt-2">
            {canVerify && existing && (
              <button className="vb-btn ghost text-sm" onClick={askDelete} disabled={busy}
                data-testid="emergency-report-delete"
                style={{ color: "#B91C1C", marginRight: "auto" }}>
                <Trash2 size={14} /> Delete
              </button>
            )}
            <button className="vb-btn ghost text-sm" onClick={onClose} disabled={busy}>Cancel</button>
            {canVerify && existing && existing.status !== "verified" && (
              <button className="vb-btn ghost text-sm" onClick={verify}
                data-testid="emergency-report-verify">
                <ShieldCheck size={14} /> Verify
              </button>
            )}
            <button className="vb-btn text-sm" onClick={submit} disabled={busy}
              data-testid="emergency-report-save">
              {busy ? "Saving…" : existing ? "Save changes" : "Create report"}
            </button>
          </div>
        </div>
      </div>
      <ConfirmDialog
        open={verifyConfirmOpen}
        title="Verify this report?"
        message="Marking the report as verified will lock further edits — only owners and managers can amend afterwards."
        confirmLabel="Verify report"
        busy={verifyBusy}
        onConfirm={doVerify}
        onCancel={() => setVerifyConfirmOpen(false)}
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete this Emergency Response Report?"
        message="This will permanently delete the report AND cascade-delete any linked NCRs along with their linked CARs. This cannot be undone."
        confirmLabel="Delete everything"
        busy={deleteBusy}
        onConfirm={doDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </div>,
    document.body,
  );
}

function SectionLabel({ children }) {
  return (
    <div className="text-xs font-bold uppercase tracking-wider mt-2 pb-1"
      style={{ color: "var(--vb-text-2)", borderBottom: "1px solid var(--vb-border)" }}>
      {children}
    </div>
  );
}
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1 uppercase tracking-wider"
        style={{ color: "var(--vb-text-2)" }}>{label}</label>
      {children}
    </div>
  );
}

// =====================================================================
//  TAB: NCR REPORTS
// =====================================================================
function NCRReports({ canReport, canVerify, activeSiteId, isAllSites, visibleSites, users, prefillFromReport, onConsumePrefill, onRaiseCAR }) {
  const [ncrs, setNcrs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = isAllSites ? "" : `?site_id=${activeSiteId}`;
      const res = await api.get(`/emergency/ncr${q}`);
      setNcrs(res.data.ncrs || []);
    } catch (e) {
      console.error("NCR load failed:", e);
      setErr("Failed to load NCRs");
    } finally { setLoading(false); }
  }, [activeSiteId, isAllSites]);
  useEffect(() => { load(); }, [load]);

  // Auto-open form when a prefill is requested by the Response tab
  useEffect(() => {
    if (prefillFromReport) setEditing("new");
  }, [prefillFromReport]);

  const sitesById = useMemo(
    () => Object.fromEntries(visibleSites.map((s) => [s.id, s])),
    [visibleSites],
  );

  return (
    <div data-testid="emergency-ncr-tab">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm opacity-70 max-w-2xl">
          Nonconforming Product Report per ISO 22000 VB-P-089 (F01). NCR No is auto-assigned
          (NCR/YY/NN). Link an NCR to an existing Emergency Response Report when applicable.
        </p>
        {canReport && (
          <button className="vb-btn text-sm" onClick={() => setEditing("new")}
            data-testid="emergency-ncr-add"
            disabled={isAllSites || !activeSiteId}
            title={(isAllSites || !activeSiteId) ? "Select a specific site in the header to enable this button" : "Raise a new Nonconforming Product Report"}>
            <Plus size={14} /> New NCR
          </button>
        )}
      </div>
      {(isAllSites || !activeSiteId) && canReport && (
        <div className="vb-pill warn mb-3 text-xs" data-testid="emergency-ncr-site-hint">
          Heads up — please select a specific site in the header to enable the “New NCR” button.
        </div>
      )}
      {err && <div className="vb-pill err mb-3">{err}</div>}
      {loading && <div className="text-sm opacity-60">Loading…</div>}
      {!loading && ncrs.length === 0 && (
        <div className="vb-card p-8 text-center opacity-70">No NCRs raised yet.</div>
      )}
      {!loading && ncrs.length > 0 && (
        <div className="grid gap-2">
          {ncrs.map((n) => (
            <NCRListItem key={n.id} n={n}
              siteName={sitesById[n.site_id]?.name || n.site_name}
              onOpen={() => setEditing(n)} />
          ))}
        </div>
      )}

      <NCRModal
        isOpen={editing !== null}
        existing={editing === "new" ? null : editing}
        siteId={editing === "new" ? activeSiteId : editing?.site_id}
        users={users}
        canVerify={canVerify}
        prefillFromReport={editing === "new" ? prefillFromReport : null}
        onClose={() => { setEditing(null); onConsumePrefill?.(); }}
        onSaved={(saved, opts) => {
          setEditing(null);
          onConsumePrefill?.();
          load();
          if (opts?.deleted) return;
          if (opts?.raiseCar && saved) onRaiseCAR?.(saved);
        }}
      />
    </div>
  );
}

function NCRListItem({ n, siteName, onOpen }) {
  const verified = n.status === "verified";
  const downloadPdf = async (e) => {
    e.stopPropagation();
    try {
      const res = await api.get(`/reports/emergency/ncr/${n.id}/pdf`, { responseType: "blob" });
      const cd = res.headers?.["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      downloadBlob(res.data, m ? m[1] : "ncr.pdf");
    } catch (err) { console.error("NCR PDF failed:", err); }
  };
  return (
    <button onClick={onOpen} className="vb-card p-3 text-left hover:shadow-md transition w-full"
      data-testid={`emergency-ncr-${n.id}`}>
      <div className="flex items-start gap-3">
        <div className="rounded-full p-2 flex-shrink-0"
          style={{ background: verified ? "#4F8A3A" : "#783D19", color: "white" }}>
          <FileWarning size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm" style={{ color: "var(--vb-text-2)" }}>
              {n.ncr_no}
            </span>
            {verified
              ? <span className="vb-pill ok text-[10px]">Verified</span>
              : <span className="vb-pill warn text-[10px]">Draft</span>}
            {n.emergency_report_id && (
              <span className="vb-pill text-[10px] inline-flex items-center gap-1">
                <Siren size={10} /> Emergency-linked
              </span>
            )}
          </div>
          <div className="text-xs opacity-70 mt-1 line-clamp-1">
            {siteName} · {n.reporting_date} · {(n.types || []).join(", ")}
          </div>
          <div className="text-xs opacity-60 mt-0.5 line-clamp-1">{n.description}</div>
        </div>
        <button onClick={downloadPdf} className="vb-btn ghost text-sm flex-shrink-0"
          data-testid={`emergency-ncr-pdf-${n.id}`}>
          PDF
        </button>
      </div>
    </button>
  );
}

function NCRModal({ isOpen, existing, siteId, users, canVerify, prefillFromReport, onClose, onSaved }) {
  const empty = {
    reporting_date: new Date().toISOString().slice(0, 10),
    department: "", types: [], other_type_text: "",
    description: "", identified_by_id: "", disposition: "",
    other_disposition_text: "", target_completion_date: "",
    emergency_report_id: "",
  };
  const [form, setForm] = useState(empty);
  const [verifyState, setVerifyState] = useState({ car_raised: false, car_no: "", car_forwarded: false });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [verifyConfirmOpen, setVerifyConfirmOpen] = useState(false);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [raiseCarChoice, setRaiseCarChoice] = useState(null); // null | true | false
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (existing) {
      setForm({
        ...empty, ...existing,
        identified_by_id: existing.identified_by_id || "",
        disposition: existing.disposition || "",
        emergency_report_id: existing.emergency_report_id || "",
      });
      setVerifyState({
        car_raised: !!existing.car_raised,
        car_no: existing.car_no || "",
        car_forwarded: !!existing.car_forwarded,
      });
    } else if (prefillFromReport) {
      setForm({
        ...empty,
        types: ["emergency_incident"],
        description: `Emergency incident: ${prefillFromReport.nature}. Affected product: ${prefillFromReport.affected_product_name}${prefillFromReport.affected_product_quantity ? ` (${prefillFromReport.affected_product_quantity})` : ""}.`,
        emergency_report_id: prefillFromReport.id,
        department: "Production",
      });
    } else {
      setForm(empty);
    }
    setErr("");
    setVerifyConfirmOpen(false);
    setRaiseCarChoice(null);
    setDeleteConfirmOpen(false);
  }, [isOpen, existing, prefillFromReport]);    // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const toggleType = (code) => {
    const has = form.types.includes(code);
    set("types", has ? form.types.filter((t) => t !== code) : [...form.types, code]);
  };

  const submit = async () => {
    if (form.types.length === 0) { setErr("Pick at least one type."); return; }
    if (!form.description.trim()) { setErr("Description is required."); return; }
    if (!siteId) { setErr("No site selected."); return; }
    setBusy(true); setErr("");
    try {
      const body = {
        ...form, site_id: siteId,
        identified_by_id: form.identified_by_id || null,
        disposition: form.disposition || null,
        emergency_report_id: form.emergency_report_id || null,
      };
      let saved;
      if (existing) {
        await api.put(`/emergency/ncr/${existing.id}`, body);
        const r = await api.get(`/emergency/ncr/${existing.id}`);
        saved = r.data;
      } else {
        const r = await api.post("/emergency/ncr", body);
        saved = r.data;
      }
      onSaved(saved, { raiseCar: raiseCarChoice === true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const verify = async () => {
    if (!existing) return;
    setVerifyConfirmOpen(true);
  };

  const doVerify = async () => {
    setVerifyBusy(true);
    try {
      await api.post(`/emergency/ncr/${existing.id}/verify`, verifyState);
      setVerifyConfirmOpen(false);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Verify failed");
      setVerifyConfirmOpen(false);
    } finally { setVerifyBusy(false); }
  };

  const askDelete = () => { if (existing) setDeleteConfirmOpen(true); };
  const doDelete = async () => {
    setDeleteBusy(true);
    try {
      await api.delete(`/emergency/ncr/${existing.id}`);
      setDeleteConfirmOpen(false);
      onSaved(null, { deleted: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed");
      setDeleteConfirmOpen(false);
    } finally { setDeleteBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(60, 40, 24, 0.6)", zIndex: 9999 }}
      data-testid="emergency-ncr-modal">
      <div className="vb-card w-full p-5 overflow-y-auto"
        style={{ maxWidth: "min(720px, 95vw)", maxHeight: "92vh" }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>
            {existing ? `NCR ${existing.ncr_no}` : "New Nonconforming Product Report"}
            <span className="ml-2 text-xs opacity-60 font-normal">VB-P-089 (F01)</span>
          </h2>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X size={20} /></button>
        </div>

        {prefillFromReport && !existing && (
          <div className="vb-pill mb-4 text-xs inline-flex items-center gap-2"
            style={{ background: "var(--vb-bg-2)" }}>
            <Siren size={12} /> Linked to Emergency Report: {prefillFromReport.nature}
          </div>
        )}

        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Reporting date">
              <input type="date" className="vb-input" value={form.reporting_date}
                onChange={(e) => set("reporting_date", e.target.value)} />
            </Field>
            <Field label="Department">
              <input className="vb-input" value={form.department}
                onChange={(e) => set("department", e.target.value)} />
            </Field>
          </div>

          <SectionLabel>Types of Nonconformity / 不合格类型</SectionLabel>
          <div className="grid grid-cols-1 gap-2">
            {NCR_TYPES.map((t) => (
              <label key={t.code} className="text-sm inline-flex items-center gap-2">
                <input type="checkbox" checked={form.types.includes(t.code)}
                  onChange={() => toggleType(t.code)}
                  data-testid={`emergency-ncr-type-${t.code}`} />
                {t.label}
              </label>
            ))}
          </div>
          {form.types.includes("other") && (
            <Field label="Other (specify)">
              <input className="vb-input" value={form.other_type_text}
                onChange={(e) => set("other_type_text", e.target.value)} />
            </Field>
          )}

          <SectionLabel>Description / 描述</SectionLabel>
          <Field label="Description of nonconformity">
            <textarea className="vb-input" rows={4} value={form.description}
              onChange={(e) => set("description", e.target.value)}
              data-testid="emergency-ncr-description" />
          </Field>
          <Field label="Identified by">
            <select className="vb-input" value={form.identified_by_id}
              onChange={(e) => set("identified_by_id", e.target.value)}>
              <option value="">— Select —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>

          <SectionLabel>Disposition / 处置方式</SectionLabel>
          <Field label="Disposition">
            <select className="vb-input" value={form.disposition}
              onChange={(e) => set("disposition", e.target.value)}
              data-testid="emergency-ncr-disposition">
              <option value="">— Select —</option>
              {DISPOSITION_OPTIONS.map((d) => <option key={d.code} value={d.code}>{d.label}</option>)}
            </select>
          </Field>
          {form.disposition === "other" && (
            <Field label="Other (specify)">
              <input className="vb-input" value={form.other_disposition_text}
                onChange={(e) => set("other_disposition_text", e.target.value)} />
            </Field>
          )}

          <Field label="Target completion date">
            <input type="date" className="vb-input" value={form.target_completion_date}
              onChange={(e) => set("target_completion_date", e.target.value)} />
          </Field>

          <SectionLabel>CAR / 纠正措施</SectionLabel>
          {existing?.verified_by_name && (
            <div className="vb-card p-3" style={{ background: "var(--vb-bg-2)" }}>
              <div className="text-xs font-bold uppercase tracking-wider mb-1">Verified</div>
              <div className="text-xs opacity-80">
                By <b>{existing.verified_by_name}</b> on {(existing.verified_at || "").slice(0, 10)}
              </div>
              <div className="text-xs mt-1">
                CAR raised: {existing.car_raised ? "Yes" : "No"}
                {existing.car_no && ` · ${existing.car_no}`}
                · Forwarded: {existing.car_forwarded ? "Yes" : "No"}
              </div>
            </div>
          )}
          {canVerify && existing && existing.status !== "verified" && (
            <div className="vb-card p-3" style={{ background: "var(--vb-bg-2)" }}>
              <div className="text-xs font-bold uppercase tracking-wider mb-2">Verification (CAR)</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                <Field label="CAR No.">
                  <input className="vb-input" value={verifyState.car_no}
                    onChange={(e) => setVerifyState({ ...verifyState, car_no: e.target.value })} />
                </Field>
                <div className="flex flex-col gap-2">
                  <label className="text-sm inline-flex items-center gap-1.5">
                    <input type="checkbox" checked={verifyState.car_raised}
                      onChange={(e) => setVerifyState({ ...verifyState, car_raised: e.target.checked })} />
                    CAR raised
                  </label>
                  <label className="text-sm inline-flex items-center gap-1.5">
                    <input type="checkbox" checked={verifyState.car_forwarded}
                      onChange={(e) => setVerifyState({ ...verifyState, car_forwarded: e.target.checked })} />
                    CAR forwarded
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Raise CAR prompt — only when CAR not yet linked */}
          {!existing?.car_id && (
            <div className="vb-card p-3 mt-2"
              style={{ background: "var(--vb-bg-2)", borderLeft: "3px solid var(--vb-accent)" }}
              data-testid="emergency-ncr-raise-car-block">
              <div className="text-sm font-semibold mb-1" style={{ color: "var(--vb-text-2)" }}>
                Will a Corrective Action Report (CAR) be raised?
              </div>
              <div className="text-xs opacity-70 mb-2">
                Choose <b>Yes</b> to save this NCR and open the linked CAR form immediately
                (VB-P-101). Choose <b>No</b> to save this NCR only — you can still raise a
                CAR later from the CAR Reports tab.
              </div>
              <div className="flex gap-4">
                <label className="text-sm inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="raise-car" checked={raiseCarChoice === true}
                    onChange={() => setRaiseCarChoice(true)}
                    data-testid="emergency-ncr-raise-car-yes" /> Yes — raise a CAR
                </label>
                <label className="text-sm inline-flex items-center gap-1.5 cursor-pointer">
                  <input type="radio" name="raise-car" checked={raiseCarChoice === false}
                    onChange={() => setRaiseCarChoice(false)}
                    data-testid="emergency-ncr-raise-car-no" /> No — NCR only
                </label>
              </div>
            </div>
          )}
          {existing?.car_id && (
            <div className="vb-pill text-xs inline-flex items-center gap-1"
              style={{ background: "var(--vb-bg-2)" }}>
              <FileWarning size={11} /> Linked to CAR {existing.car_no || existing.car_id}
            </div>
          )}
        </div>

        {err && <div className="vb-pill err mt-4" data-testid="emergency-ncr-error">{err}</div>}
        <div className="flex gap-2 justify-end pt-4 mt-2 border-t"
          style={{ borderColor: "var(--vb-border)" }}>
          {canVerify && existing && (
            <button className="vb-btn ghost text-sm" onClick={askDelete} disabled={busy}
              data-testid="emergency-ncr-delete"
              style={{ color: "#B91C1C", marginRight: "auto" }}>
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button className="vb-btn ghost text-sm" onClick={onClose} disabled={busy}>Cancel</button>
          {canVerify && existing && existing.status !== "verified" && (
            <button className="vb-btn ghost text-sm" onClick={verify}
              data-testid="emergency-ncr-verify">
              <ShieldCheck size={14} /> Verify
            </button>
          )}
          <button className="vb-btn text-sm" onClick={submit} disabled={busy}
            data-testid="emergency-ncr-save">
            {busy ? "Saving…" : existing ? "Save changes" : "Raise NCR"}
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={verifyConfirmOpen}
        title="Verify this NCR?"
        message="Verifying will finalise the disposition and lock the report. CAR fields above will be saved as part of the verification."
        confirmLabel="Verify NCR"
        busy={verifyBusy}
        onConfirm={doVerify}
        onCancel={() => setVerifyConfirmOpen(false)}
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete this NCR?"
        message={
          existing?.car_id
            ? "This will permanently delete the NCR AND its linked CAR. This cannot be undone."
            : "This will permanently delete the NCR. This cannot be undone."
        }
        confirmLabel="Delete NCR"
        busy={deleteBusy}
        onConfirm={doDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </div>,
    document.body,
  );
}

// =====================================================================
//  TAB: CAR REPORTS (VB-P-101 F01)
// =====================================================================
function CARReports({ canReport, canVerify, activeSiteId, isAllSites, visibleSites, users, prefillFromNcr, onConsumeCarPrefill }) {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = isAllSites ? "" : `?site_id=${activeSiteId}`;
      const res = await api.get(`/emergency/car${q}`);
      setCars(res.data.cars || []);
    } catch (e) {
      console.error("CAR load failed:", e);
      setErr("Failed to load CARs");
    } finally { setLoading(false); }
  }, [activeSiteId, isAllSites]);
  useEffect(() => { load(); }, [load]);

  // Auto-open form when a CAR prefill is requested by the NCR tab.
  useEffect(() => {
    if (prefillFromNcr) setEditing("new");
  }, [prefillFromNcr]);

  const sitesById = useMemo(
    () => Object.fromEntries(visibleSites.map((s) => [s.id, s])),
    [visibleSites],
  );

  return (
    <div data-testid="emergency-car-tab">
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm opacity-70 max-w-2xl">
          Corrective Action Report per ISO 22000 VB-P-101 (F01). CAR No is auto-assigned
          (CAR/YY/NN). A CAR drives root-cause analysis and verification of corrective action;
          link it to an NCR for full traceability.
        </p>
        {canReport && (
          <button className="vb-btn text-sm" onClick={() => setEditing("new")}
            data-testid="emergency-car-add"
            disabled={isAllSites || !activeSiteId}
            title={(isAllSites || !activeSiteId) ? "Select a specific site in the header to enable this button" : "Raise a new Corrective Action Report"}>
            <Plus size={14} /> New CAR
          </button>
        )}
      </div>
      {(isAllSites || !activeSiteId) && canReport && (
        <div className="vb-pill warn mb-3 text-xs" data-testid="emergency-car-site-hint">
          Heads up — please select a specific site in the header to enable the “New CAR” button.
        </div>
      )}
      {err && <div className="vb-pill err mb-3">{err}</div>}
      {loading && <div className="text-sm opacity-60">Loading…</div>}
      {!loading && cars.length === 0 && (
        <div className="vb-card p-8 text-center opacity-70">No CARs raised yet.</div>
      )}
      {!loading && cars.length > 0 && (
        <div className="grid gap-2">
          {cars.map((c) => (
            <CARListItem key={c.id} c={c}
              siteName={sitesById[c.site_id]?.name || c.site_name}
              onOpen={() => setEditing(c)} />
          ))}
        </div>
      )}

      <CARModal
        isOpen={editing !== null}
        existing={editing === "new" ? null : editing}
        siteId={
          editing === "new"
            ? (prefillFromNcr?.site_id || activeSiteId)
            : editing?.site_id
        }
        users={users}
        canVerify={canVerify}
        prefillFromNcr={editing === "new" ? prefillFromNcr : null}
        onClose={() => { setEditing(null); onConsumeCarPrefill?.(); }}
        onSaved={() => { setEditing(null); onConsumeCarPrefill?.(); load(); }}
      />
    </div>
  );
}

function CARListItem({ c, siteName, onOpen }) {
  const verified = c.status === "verified";
  return (
    <button onClick={onOpen} className="vb-card p-3 text-left hover:shadow-md transition w-full"
      data-testid={`emergency-car-${c.id}`}>
      <div className="flex items-start gap-3">
        <div className="rounded-full p-2 flex-shrink-0"
          style={{ background: verified ? "#4F8A3A" : "#783D19", color: "white" }}>
          <ClipboardList size={14} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm" style={{ color: "var(--vb-text-2)" }}>
              {c.car_no}
            </span>
            {verified
              ? <span className="vb-pill ok text-[10px]">Reviewed</span>
              : <span className="vb-pill warn text-[10px]">Draft</span>}
            {c.related_ncr_no && (
              <span className="vb-pill text-[10px] inline-flex items-center gap-1">
                <FileWarning size={10} /> Linked to {c.related_ncr_no}
              </span>
            )}
          </div>
          <div className="text-xs opacity-70 mt-1 line-clamp-1">
            {siteName} · {c.reporting_date} · {(c.types || []).map((t) => CAR_TYPES.find((x) => x.code === t)?.label || t).join(", ")}
          </div>
          <div className="text-xs opacity-60 mt-0.5 line-clamp-1">{c.description_consequences}</div>
        </div>
      </div>
    </button>
  );
}

function CARModal({ isOpen, existing, siteId, users, canVerify, prefillFromNcr, onClose, onSaved }) {
  const empty = {
    reporting_date: new Date().toISOString().slice(0, 10),
    department: "",
    related_ncr_id: "",
    types: [],
    other_type_text: "",
    description_consequences: "",
    nonconformity_identified_by_id: "",
    correction: "",
    correction_target_date: "",
    root_causes: "",
    corrective_action: "",
    corrective_action_due_date: "",
  };
  const [form, setForm] = useState(empty);
  const [reviewState, setReviewState] = useState({
    effectiveness_review: "", reviewed_by_id: "", review_date: "",
    fsms_changes_required: false, fsms_changes_description: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [reviewConfirmOpen, setReviewConfirmOpen] = useState(false);
  const [reviewBusy, setReviewBusy] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    if (existing) {
      setForm({
        ...empty, ...existing,
        related_ncr_id: existing.related_ncr_id || "",
        nonconformity_identified_by_id: existing.nonconformity_identified_by_id || "",
      });
      setReviewState({
        effectiveness_review: existing.effectiveness_review || "",
        reviewed_by_id: existing.reviewed_by_id || "",
        review_date: existing.review_date || "",
        fsms_changes_required: !!existing.fsms_changes_required,
        fsms_changes_description: existing.fsms_changes_description || "",
      });
    } else if (prefillFromNcr) {
      // Carry NCR context into the new CAR.
      const types = (prefillFromNcr.types || []).includes("emergency_incident")
        ? ["internal_audit"]
        : ["internal_audit"];
      setForm({
        ...empty,
        related_ncr_id: prefillFromNcr.id,
        department: prefillFromNcr.department || "",
        types,
        description_consequences: `Linked to NCR ${prefillFromNcr.ncr_no || ""}. ${prefillFromNcr.description || ""}`.trim(),
        correction: prefillFromNcr.disposition ? `Disposition: ${prefillFromNcr.disposition}${prefillFromNcr.other_disposition_text ? ` (${prefillFromNcr.other_disposition_text})` : ""}.` : "",
        correction_target_date: prefillFromNcr.target_completion_date || "",
      });
    } else {
      setForm(empty);
    }
    setErr("");
    setReviewConfirmOpen(false);
    setDeleteConfirmOpen(false);
  }, [isOpen, existing, prefillFromNcr]);    // eslint-disable-line react-hooks/exhaustive-deps

  if (!isOpen) return null;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const toggleType = (code) => {
    const has = form.types.includes(code);
    set("types", has ? form.types.filter((t) => t !== code) : [...form.types, code]);
  };

  const submit = async () => {
    if (form.types.length === 0) { setErr("Pick at least one type."); return; }
    if (!form.description_consequences.trim()) { setErr("Description & consequences is required."); return; }
    if (!siteId) { setErr("No site selected."); return; }
    setBusy(true); setErr("");
    try {
      const body = {
        site_id: siteId,
        related_ncr_id: form.related_ncr_id || null,
        initiator_id: null,
        department: form.department,
        reporting_date: form.reporting_date,
        types: form.types,
        other_type_text: form.other_type_text,
        description_consequences: form.description_consequences,
        nonconformity_identified_by_id: form.nonconformity_identified_by_id || null,
        correction: form.correction,
        correction_target_date: form.correction_target_date,
        root_causes: form.root_causes,
        corrective_action: form.corrective_action,
        corrective_action_due_date: form.corrective_action_due_date,
      };
      if (existing) {
        await api.put(`/emergency/car/${existing.id}`, body);
      } else {
        await api.post("/emergency/car", body);
      }
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const askReview = () => { if (existing) setReviewConfirmOpen(true); };
  const doReview = async () => {
    setReviewBusy(true);
    try {
      await api.post(`/emergency/car/${existing.id}/review`, {
        effectiveness_review: reviewState.effectiveness_review,
        reviewed_by_id: reviewState.reviewed_by_id || null,
        review_date: reviewState.review_date,
        fsms_changes_required: reviewState.fsms_changes_required,
        fsms_changes_description: reviewState.fsms_changes_description,
      });
      setReviewConfirmOpen(false);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Review failed");
      setReviewConfirmOpen(false);
    } finally { setReviewBusy(false); }
  };

  const askDelete = () => { if (existing) setDeleteConfirmOpen(true); };
  const doDelete = async () => {
    setDeleteBusy(true);
    try {
      await api.delete(`/emergency/car/${existing.id}`);
      setDeleteConfirmOpen(false);
      onSaved();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Delete failed");
      setDeleteConfirmOpen(false);
    } finally { setDeleteBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center p-4"
      style={{ background: "rgba(60, 40, 24, 0.6)", zIndex: 9999 }}
      data-testid="emergency-car-modal">
      <div className="vb-card w-full p-5 overflow-y-auto"
        style={{ maxWidth: "min(720px, 95vw)", maxHeight: "92vh" }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="vb-heading text-xl" style={{ color: "var(--vb-text-2)" }}>
            {existing ? `CAR ${existing.car_no}` : "New Corrective Action Report"}
            <span className="ml-2 text-xs opacity-60 font-normal">VB-P-101 (F01)</span>
          </h2>
          <button onClick={onClose} className="opacity-60 hover:opacity-100"><X size={20} /></button>
        </div>

        {form.related_ncr_id && (
          <div className="vb-pill mb-4 text-xs inline-flex items-center gap-2"
            style={{ background: "var(--vb-bg-2)" }}>
            <FileWarning size={12} />
            Related NCR: {existing?.related_ncr_no || prefillFromNcr?.ncr_no || form.related_ncr_id}
          </div>
        )}

        <div className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Reporting date">
              <input type="date" className="vb-input" value={form.reporting_date}
                onChange={(e) => set("reporting_date", e.target.value)}
                data-testid="emergency-car-reporting-date" />
            </Field>
            <Field label="Department">
              <input className="vb-input" value={form.department}
                onChange={(e) => set("department", e.target.value)}
                data-testid="emergency-car-department" />
            </Field>
          </div>

          <SectionLabel>Types of Nonconformity / 不合格类型</SectionLabel>
          <div className="grid grid-cols-1 gap-2">
            {CAR_TYPES.map((t) => (
              <label key={t.code} className="text-sm inline-flex items-center gap-2">
                <input type="checkbox" checked={form.types.includes(t.code)}
                  onChange={() => toggleType(t.code)}
                  data-testid={`emergency-car-type-${t.code}`} />
                {t.label}
              </label>
            ))}
          </div>
          {form.types.includes("other") && (
            <Field label="Other (specify)">
              <input className="vb-input" value={form.other_type_text}
                onChange={(e) => set("other_type_text", e.target.value)} />
            </Field>
          )}

          <SectionLabel>Description & Consequences / 描述与后果</SectionLabel>
          <Field label="Description and consequences as a result of nonconformity">
            <textarea className="vb-input" rows={4} value={form.description_consequences}
              onChange={(e) => set("description_consequences", e.target.value)}
              data-testid="emergency-car-description" />
          </Field>
          <Field label="Nonconformity identified by">
            <select className="vb-input" value={form.nonconformity_identified_by_id}
              onChange={(e) => set("nonconformity_identified_by_id", e.target.value)}>
              <option value="">— Select —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          </Field>

          <SectionLabel>Correction / 纠正</SectionLabel>
          <Field label="Correction towards the nonconformity">
            <textarea className="vb-input" rows={3} value={form.correction}
              onChange={(e) => set("correction", e.target.value)}
              data-testid="emergency-car-correction" />
          </Field>
          <Field label="Target completion date for correction">
            <input type="date" className="vb-input" value={form.correction_target_date}
              onChange={(e) => set("correction_target_date", e.target.value)} />
          </Field>

          <SectionLabel>Root Causes / 根本原因</SectionLabel>
          <Field label="Determining the causes of nonconformity (highlight the root cause(s) identified)">
            <textarea className="vb-input" rows={3} value={form.root_causes}
              onChange={(e) => set("root_causes", e.target.value)}
              data-testid="emergency-car-root-causes" />
          </Field>

          <SectionLabel>Corrective Action / 纠正措施</SectionLabel>
          <Field label="Corrective action determined to prevent recurrence or occurrence elsewhere">
            <textarea className="vb-input" rows={3} value={form.corrective_action}
              onChange={(e) => set("corrective_action", e.target.value)}
              data-testid="emergency-car-corrective-action" />
          </Field>
          <Field label="Due date for implementation of corrective actions">
            <input type="date" className="vb-input" value={form.corrective_action_due_date}
              onChange={(e) => set("corrective_action_due_date", e.target.value)} />
          </Field>

          <SectionLabel>Effectiveness Review / 有效性审查</SectionLabel>
          {existing?.status === "verified" ? (
            <div className="vb-card p-3" style={{ background: "var(--vb-bg-2)" }}>
              <div className="text-xs font-bold uppercase tracking-wider mb-1">Reviewed</div>
              <div className="text-xs opacity-80">
                By <b>{existing.reviewed_by_name || "—"}</b> on {existing.review_date || "—"}
              </div>
              <div className="text-sm mt-2 whitespace-pre-wrap">{existing.effectiveness_review || "—"}</div>
              {existing.fsms_changes_required && (
                <div className="text-xs mt-2">
                  <b>FSMS changes:</b> {existing.fsms_changes_description || "(no detail)"}
                </div>
              )}
            </div>
          ) : canVerify && existing ? (
            <div className="vb-card p-3" style={{ background: "var(--vb-bg-2)" }}>
              <Field label="Review the effectiveness of corrective actions taken">
                <textarea className="vb-input" rows={3} value={reviewState.effectiveness_review}
                  onChange={(e) => setReviewState({ ...reviewState, effectiveness_review: e.target.value })}
                  data-testid="emergency-car-effectiveness" />
              </Field>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Field label="Reviewed by">
                  <select className="vb-input" value={reviewState.reviewed_by_id}
                    onChange={(e) => setReviewState({ ...reviewState, reviewed_by_id: e.target.value })}>
                    <option value="">— Self —</option>
                    {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </Field>
                <Field label="Review date">
                  <input type="date" className="vb-input" value={reviewState.review_date}
                    onChange={(e) => setReviewState({ ...reviewState, review_date: e.target.value })} />
                </Field>
              </div>
              <label className="text-sm inline-flex items-center gap-1.5 mt-3">
                <input type="checkbox" checked={reviewState.fsms_changes_required}
                  onChange={(e) => setReviewState({ ...reviewState, fsms_changes_required: e.target.checked })}
                  data-testid="emergency-car-fsms-changed" />
                Updating of FSMS — make changes if necessary (to be determined by MR)
              </label>
              {reviewState.fsms_changes_required && (
                <Field label="Description of changes needed">
                  <textarea className="vb-input" rows={2} value={reviewState.fsms_changes_description}
                    onChange={(e) => setReviewState({ ...reviewState, fsms_changes_description: e.target.value })} />
                </Field>
              )}
            </div>
          ) : (
            <div className="text-xs opacity-60 italic">
              Effectiveness review is recorded by an owner/manager after the corrective action
              is implemented. Save this CAR first; the review block will appear here once the
              CAR exists.
            </div>
          )}
        </div>

        {err && <div className="vb-pill err mt-4" data-testid="emergency-car-error">{err}</div>}
        <div className="flex gap-2 justify-end pt-4 mt-2 border-t"
          style={{ borderColor: "var(--vb-border)" }}>
          {canVerify && existing && (
            <button className="vb-btn ghost text-sm" onClick={askDelete} disabled={busy}
              data-testid="emergency-car-delete"
              style={{ color: "#B91C1C", marginRight: "auto" }}>
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button className="vb-btn ghost text-sm" onClick={onClose} disabled={busy}>Cancel</button>
          {canVerify && existing && existing.status !== "verified" && (
            <button className="vb-btn ghost text-sm" onClick={askReview}
              data-testid="emergency-car-review">
              <ShieldCheck size={14} /> Record review
            </button>
          )}
          <button className="vb-btn text-sm" onClick={submit} disabled={busy}
            data-testid="emergency-car-save">
            {busy ? "Saving…" : existing ? "Save changes" : "Raise CAR"}
          </button>
        </div>
      </div>
      <ConfirmDialog
        open={reviewConfirmOpen}
        title="Record this review?"
        message="Recording the effectiveness review will lock the CAR. Only owners and managers can amend afterwards."
        confirmLabel="Record review"
        busy={reviewBusy}
        onConfirm={doReview}
        onCancel={() => setReviewConfirmOpen(false)}
      />
      <ConfirmDialog
        open={deleteConfirmOpen}
        title="Delete this CAR?"
        message="This will permanently delete the CAR. The linked NCR (if any) will remain — its CAR link will be cleared. This cannot be undone."
        confirmLabel="Delete CAR"
        busy={deleteBusy}
        onConfirm={doDelete}
        onCancel={() => setDeleteConfirmOpen(false)}
      />
    </div>,
    document.body,
  );
}

// =====================================================================
//  PAGE SHELL
// =====================================================================
export default function Emergency() {
  const { user } = useAuth();
  const { confirm } = useDialog();
  const { activeSiteId, isAllSites, visibleSites, loaded: sitesLoaded } = useSite();
  const [searchParams, setSearchParams] = useSearchParams();
  const validTabs = ["playbooks", "contacts", "reports", "ncr", "car"];
  const tabFromUrl = searchParams.get("tab");
  const initialTab = validTabs.includes(tabFromUrl) ? tabFromUrl : "playbooks";
  const [tab, setTabState] = useState(initialTab);

  const setTab = (next) => {
    setTabState(next);
    const params = new URLSearchParams(searchParams);
    params.set("tab", next);
    setSearchParams(params, { replace: true });
  };

  const [users, setUsers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [directoryLoading, setDirectoryLoading] = useState(true);
  const [directoryErr, setDirectoryErr] = useState("");
  const [ncrPrefill, setNcrPrefill] = useState(null);
  const [carPrefill, setCarPrefill] = useState(null);

  const canEdit = ROLE_WRITER.includes(user?.role);
  const canReport = ROLE_REPORTER.includes(user?.role);
  const canVerify = ROLE_WRITER.includes(user?.role);

  // Shared site context: when "All sites" is active, contacts/playbooks default
  // to the first visible site so something useful renders.
  const siteToLoad = isAllSites ? (visibleSites[0]?.id || null) : activeSiteId;

  // Lift contact + scenario fetching to the parent so both Playbooks and
  // Contact Directory tabs reuse the same data without double-fetching.
  const loadDirectory = useCallback(async () => {
    if (!siteToLoad) { setContacts([]); setScenarios([]); setDirectoryLoading(false); return; }
    setDirectoryLoading(true); setDirectoryErr("");
    try {
      const [c, p] = await Promise.all([
        api.get(`/emergency/contacts?site_id=${siteToLoad}`),
        api.get("/emergency/playbooks"),
      ]);
      setContacts(c.data.contacts || []);
      setScenarios(p.data.scenarios || []);
    } catch (e) {
      setDirectoryErr("Failed to load emergency directory");
    } finally { setDirectoryLoading(false); }
  }, [siteToLoad]);

  useEffect(() => { if (sitesLoaded) loadDirectory(); }, [sitesLoaded, loadDirectory]);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/users");
        setUsers((res.data.users || []).filter((u) => !u.is_archived));
      } catch (e) { console.error("Users load failed:", e); }
    })();
  }, []);

  // Listen for the "Raise NCR" button inside playbook cards → jump to NCR tab.
  useEffect(() => {
    const jumpToNcr = () => setTab("ncr");
    window.addEventListener("emergency-jump-ncr", jumpToNcr);
    return () => window.removeEventListener("emergency-jump-ncr", jumpToNcr);
    // eslint-disable-next-line
  }, []);

  const promptNCR = async (savedReport) => {
    if (await confirm({
      title: "Raise a linked NCR?",
      message: "Product was affected. Raise a linked Nonconforming Product Report now?",
      confirmLabel: "Raise NCR",
    })) {
      setNcrPrefill(savedReport);
      setTab("ncr");
    }
  };

  // After an NCR is saved and the user opted in to raise a CAR.
  const onRaiseCAR = (savedNcr) => {
    setCarPrefill(savedNcr);
    setTab("car");
  };

  const byCategory = useMemo(() => {
    const g = {};
    contacts.forEach((c) => { (g[c.category] = g[c.category] || []).push(c); });
    return g;
  }, [contacts]);

  return (
    <div className="vb-fade-in" data-testid="emergency-page">
      <h1 className="vb-heading text-3xl mb-1" style={{ color: "var(--vb-text-2)" }}>
        Incident Response
      </h1>
      <p className="opacity-70 mb-4 max-w-3xl">
        Incident playbooks, on-call directory, and the full ERR → NCR → CAR audit trail.
        Aligned with ISO 22000 VB-P-084 / VB-P-089 / VB-P-101. Staff can read contacts;
        supervisors and managers can file reports; only managers/owners can verify.
      </p>

      {/* Crisis-first ribbon — Police / Fire / Ambulance always visible above tabs. */}
      {sitesLoaded && !directoryLoading && (
        <div className="mb-4" data-testid="emergency-crisis-ribbon">
          <CriticalBar byCategory={byCategory} />
          {isAllSites && (
            <div className="text-xs mt-2 opacity-70 inline-flex items-center gap-1">
              <MapPin size={12} /> Showing directory for {visibleSites[0]?.name || "first site"}.
              Pick a site in the header to view its directory.
            </div>
          )}
          {directoryErr && <div className="vb-pill err mt-2" data-testid="emergency-directory-error">{directoryErr}</div>}
        </div>
      )}

      {/* Tabs */}
      <div className="vb-tab-strip mb-5 border-b" style={{ borderColor: "var(--vb-border)" }} data-testid="emergency-tabs">
        <TabBtn active={tab === "playbooks"} onClick={() => setTab("playbooks")} testid="emergency-tab-playbooks">
          <Siren size={14} /> Incidents Playbooks
        </TabBtn>
        <TabBtn active={tab === "contacts"} onClick={() => setTab("contacts")} testid="emergency-tab-contacts">
          <Phone size={14} /> Contact Directory
        </TabBtn>
        <TabBtn active={tab === "reports"} onClick={() => setTab("reports")} testid="emergency-tab-reports">
          <Siren size={14} /> Response Reports
        </TabBtn>
        <TabBtn active={tab === "ncr"} onClick={() => setTab("ncr")} testid="emergency-tab-ncr">
          <FileWarning size={14} /> NCR Reports
        </TabBtn>
        <TabBtn active={tab === "car"} onClick={() => setTab("car")} testid="emergency-tab-car">
          <ClipboardList size={14} /> CAR Reports
        </TabBtn>
      </div>

      {!sitesLoaded && <div className="text-sm opacity-60">Loading sites…</div>}

      {sitesLoaded && tab === "playbooks" && (
        <IncidentPlaybooksTab
          contacts={contacts} scenarios={scenarios}
          loading={directoryLoading} siteId={siteToLoad}
          canEdit={canEdit} onChanged={loadDirectory}
        />
      )}
      {sitesLoaded && tab === "contacts" && (
        <ContactDirectoryTab
          contacts={contacts} loading={directoryLoading} siteId={siteToLoad}
          canEdit={canEdit} onChanged={loadDirectory}
        />
      )}
      {sitesLoaded && tab === "reports" && (
        <ResponseReports canReport={canReport} canVerify={canVerify}
          activeSiteId={activeSiteId} isAllSites={isAllSites} visibleSites={visibleSites}
          users={users} onPromptNCR={promptNCR} />
      )}
      {sitesLoaded && tab === "ncr" && (
        <NCRReports canReport={canReport} canVerify={canVerify}
          activeSiteId={activeSiteId} isAllSites={isAllSites} visibleSites={visibleSites}
          users={users} prefillFromReport={ncrPrefill}
          onConsumePrefill={() => setNcrPrefill(null)}
          onRaiseCAR={onRaiseCAR} />
      )}
      {sitesLoaded && tab === "car" && (
        <CARReports canReport={canReport} canVerify={canVerify}
          activeSiteId={activeSiteId} isAllSites={isAllSites} visibleSites={visibleSites}
          users={users} prefillFromNcr={carPrefill}
          onConsumeCarPrefill={() => setCarPrefill(null)} />
      )}
    </div>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="px-4 py-2 text-sm font-semibold inline-flex items-center gap-2 transition-all"
      style={{
        color: active ? "var(--vb-accent)" : "var(--vb-text-2)",
        borderBottom: active ? "2px solid var(--vb-accent)" : "2px solid transparent",
        opacity: active ? 1 : 0.65,
        marginBottom: "-1px",
      }}
    >
      {children}
    </button>
  );
}
